import {Injectable, OnDestroy, Inject} from '@angular/core';

import {Observable, ReplaySubject, of, merge, interval, Subject} from 'rxjs';
import {concatMap, map, takeUntil} from 'rxjs/operators';

import {EntryStore} from '@stores/entry.store';

import {ExpireEntriesAction} from '@actions/expire-entries.action';

import * as APIM from '@common/utils/dist/models/api-messages.js';
import * as PL2 from '@common/utils/dist/models/models-pl2.js';
import {KeyUtilsPL2 as KU} from '@common/utils/dist/utils/key-utils-pl2.js';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';

import {undoTime} from '@constants/app.config';
import {EntryEventChain} from './entry-event-handlers/entry-event-chain';

import {
  EventAPIService,
  EventAPIServiceToken,
} from '@services/event-api.service';
import {
  AssignmentCompleteUpdateEventInputBuilder,
  EntryBatchUpdateEventInputBuilder,
  EntryCreateEventInputBuilder,
  EntryDeepDeleteEventInputBuilder,
} from '@services/events/entry-events';

@Injectable({
  providedIn: 'root',
})
export class EntryReactor implements OnDestroy {
  private _flush$ = new Subject<boolean>();

  private _destroyed$ = new ReplaySubject<boolean>();

  constructor(
    private entryStore: EntryStore,
    private expireEntriesAction: ExpireEntriesAction,
    @Inject(EventAPIServiceToken) private eventApiService: EventAPIService,
  ) {
    merge(
      merge(interval(undoTime), this.entryStore.debounceState$),
      this._flush$,
    )
      .pipe(
        map(() => this.entryStore.state()),
        concatMap((entryState) => {
          const chain = new EntryEventChain(this.entryStore);
          const events = chain.execute(entryState);
          return merge(
            this._sendChanges(events.changes),
            this._expire(events.expired),
          );
        }),
        takeUntil(this._destroyed$),
      )
      .subscribe(
        () => {},
        (err) => {
          console.error('[EntryReactor]caught', err);
        },
        () => {
          console.debug('[EntryReactor]end!!!');
        },
      );
  }

  ngOnDestroy() {
    this._flush$.next(true);
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  private _sendChanges(
    changes: Partial<
      Record<APIM.EventIntent, Array<PL2.AnyPartialEntryWithId>>
    >,
  ): Observable<string | void> {
    const intents = Object.keys(changes);
    if (U.isEmpty(intents)) {
      return of(void 0);
    }
    return merge(
      ...intents.map((int) => {
        if (U.isEmpty(changes[int])) {
          return of(void 0);
        }
        /**
         * In the unlikely event that the entry state is corrupted
         * let's just make sure the corrupted entry can't get through.
         */
        const input = changes[int].filter((ch) => KU.isPrimaryKey(ch));

        /**
         * if there is anything corrupted we can't even expire the
         * thing since we don't have a primary key.
         */
        if (input.length < changes[int].length) {
          console.error('A corrupted entry was omitted', changes[int]);
        }

        switch (Number(int)) {
          case APIM.EventIntent.EntryCreate:
            return this.eventApiService.sendEvent(
              new EntryCreateEventInputBuilder(input),
            );
          case APIM.EventIntent.EntryBatchUpdate:
            return this.eventApiService.sendEvent(
              new EntryBatchUpdateEventInputBuilder(input),
            );
          case APIM.EventIntent.EntryDeepDelete:
            return this.eventApiService.sendEvent(
              new EntryDeepDeleteEventInputBuilder(input),
            );
          case APIM.EventIntent.AssignmentCompleteUpdate:
            return this.eventApiService.sendEvent(
              new AssignmentCompleteUpdateEventInputBuilder(input),
            );
          default:
            throw new Error(`Unhandled event intent: ${int}`);
        }
      }),
    );
  }

  private _expire(pKs: string[]): Observable<any> {
    if (pKs.length > 0) {
      return this.expireEntriesAction.execute(pKs);
    }
    return of(void 0);
  }
}
